import { Help } from '@material-ui/icons';
import { Inline, Stack } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useMatch } from 'react-router-dom';
import { SuperPayRequestShipperBanner } from 'shared/modules/superpay/SuperPayRequestShipperBanner';
import { useBypassNavigationPrompt } from 'shared/routing/NavigationBlock';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useSuperPaySettings } from '../../data/SuperPayAPI';
import { ProfilePageLayout } from '../../ProfilePageLayout';
import { SUPERPAY_PROFILE_ROUTES } from '../SuperPayRoute';
import { BankDetails } from './BankDetails';
import { BankVerificationDrawer } from './BankVerificationDrawer';
import { ChangeBankAccountDrawer } from './ChangeBankAccountDrawer';
import { useTrackAccountDetailsOpened } from './useTrackAccountDetailsOpened';
import { useVerificationAvailable } from './useVerificationAvailable';

export function AccountDetailsPage() {
  const navigate = useBypassNavigationPrompt();

  const { data: settings } = useCarrierSettings();
  const hasAccountingFeature = settings?.user.has_accounting_feature;

  const { data: superPaySettings, refetch: fetchSuperPaySettings } =
    useSuperPaySettings();
  const bankAccount = superPaySettings?.bank_accounts?.[0];
  const previousBankAccount = superPaySettings?.bank_accounts?.[1];
  const verificationStatus = bankAccount?.verification_status;

  const isOnChangeBankAccount = !!useMatch(
    SUPERPAY_PROFILE_ROUTES.changeBankAccount,
  );
  const hasBankAccountAwaitingVerification =
    !!bankAccount && bankAccount.verification_status !== 'in_review';

  useVerificationAvailable(SUPERPAY_PROFILE_ROUTES.accountDetails, {
    skip: isOnChangeBankAccount || hasBankAccountAwaitingVerification,
  });

  const onDrawerClose = () => {
    navigate(
      `${SUPERPAY_PROFILE_ROUTES.accountDetails}?utm_medium=Verify Bank Account Drawer`,
    );
  };

  const onChangeBankAccountDrawerClose = () => {
    navigate(
      `${SUPERPAY_PROFILE_ROUTES.accountDetails}?utm_medium=Change Bank Account Drawer`,
      { replace: true },
    );
  };

  useTrackAccountDetailsOpened(verificationStatus);

  return (
    <ProfilePageLayout
      title="SuperPay"
      isLoading={!superPaySettings}
      actions={false}
      size="medium"
      endActions={
        <Inline verticalAlign="center" space="xxsmall">
          <Help fontSize="small" color="action" />
          <WebViewLink
            target="_blank"
            rel="noopener noreferrer"
            href="https://support.superdispatch.com/hc/en-us/categories/35595057094163-SuperPay"
          >
            How SuperPay works
          </WebViewLink>
        </Inline>
      }
    >
      <Stack space="large">
        <Box margin="auto" marginTop="medium" maxWidth="496px">
          <BankDetails
            bankAccount={bankAccount}
            previousBankAccount={previousBankAccount}
            hoursUntilUpdateAllowed={
              superPaySettings?.hours_until_update_allowed
            }
            hasAccountFeature={hasAccountingFeature}
            path="/profile/superpay/onboarding/account-details"
          />
        </Box>
        {settings?.superpay.is_invite_available && (
          <Box margin="auto" maxWidth="496px">
            <SuperPayRequestShipperBanner />
          </Box>
        )}
      </Stack>
      <BankVerificationDrawer
        bankAccount={bankAccount}
        onClose={onDrawerClose}
        onComplete={() => {
          onDrawerClose();
          void fetchSuperPaySettings();
        }}
      />
      <ChangeBankAccountDrawer
        bankAccount={bankAccount}
        onClose={onChangeBankAccountDrawerClose}
        onComplete={() => {
          onChangeBankAccountDrawerClose();
          void fetchSuperPaySettings();
        }}
      />
    </ProfilePageLayout>
  );
}
